import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  MapPin,
  Phone,
  FileText,
  Download,
  Building2,
  Calendar,
  CreditCard,
  User,
  Mail,
  ShoppingBag
} from 'lucide-react';
import Card from '../modern-components/ui/Card';
import Button from '../modern-components/ui/Button';
import Badge from '../modern-components/ui/Badge';
import { orderAPI, BASE_URL } from '../services/api';

const OrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await orderAPI.getOrderById(orderId);
      if (response.data.success) {
        setOrder(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch order details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${BASE_URL}${imageUrl}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-6 h-6" />;
      case 'confirmed':
        return <Package className="w-6 h-6" />;
      case 'dispatched':
        return <Truck className="w-6 h-6" />;
      case 'delivered':
        return <CheckCircle className="w-6 h-6" />;
      case 'cancelled':
        return <XCircle className="w-6 h-6" />;
      default:
        return <Clock className="w-6 h-6" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'dispatched':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'danger';
      default:
        return 'gray';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'dispatched':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const orderSteps = [
    { status: 'pending', label: 'Order Placed', icon: ShoppingBag, description: 'Your order has been placed' },
    { status: 'confirmed', label: 'Confirmed', icon: Package, description: 'Order confirmed by seller' },
    { status: 'dispatched', label: 'Dispatched', icon: Truck, description: 'Order is on the way' },
    { status: 'delivered', label: 'Delivered', icon: CheckCircle, description: 'Order delivered successfully' },
  ];

  const getStepStatus = (stepStatus) => {
    if (!order) return 'pending';

    const statusOrder = ['pending', 'confirmed', 'dispatched', 'delivered'];
    const currentIndex = statusOrder.indexOf(order.status);
    const stepIndex = statusOrder.indexOf(stepStatus);

    if (order.status === 'cancelled') return 'cancelled';
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 pb-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 pb-12 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {error || 'Order not found'}
          </h2>
          <p className="text-gray-600 mb-6">We couldn't find this order. Please check the order ID.</p>
          <Button onClick={() => navigate('/dashboard?tab=orders')}>Back to Orders</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="w-5 h-5" />}
          onClick={() => navigate('/dashboard?tab=orders')}
          className="mb-6 animate-fade-in"
        >
          Back to Orders
        </Button>

        {/* Order Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 animate-fade-in">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Order #{order.order_number}
                </h1>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusBgColor(order.status)}`}>
                  {order.status?.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-4 text-gray-600 text-sm">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Placed on {formatShortDate(order.created_at)}
                </span>
                <span className="flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  {formatPrice(order.total_amount)}
                </span>
              </div>
            </div>
            {order.invoice_url && (
              <a
                href={order.invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg font-medium hover:bg-primary-100 transition-colors"
              >
                <FileText className="w-5 h-5" />
                Download Invoice
                <Download className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        {/* Order Progress Tracker */}
        {order.status !== 'cancelled' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 animate-slide-up">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Order Tracking</h2>

            {/* Desktop Progress */}
            <div className="hidden md:block relative">
              {/* Progress Line */}
              <div className="absolute top-10 left-0 right-0 h-1 bg-gray-200 mx-16">
                <div
                  className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500"
                  style={{
                    width: `${
                      order.status === 'pending'
                        ? '0%'
                        : order.status === 'confirmed'
                        ? '33%'
                        : order.status === 'dispatched'
                        ? '66%'
                        : '100%'
                    }`,
                  }}
                />
              </div>

              {/* Steps */}
              <div className="relative flex justify-between">
                {orderSteps.map((step, index) => {
                  const stepStatus = getStepStatus(step.status);
                  const Icon = step.icon;

                  return (
                    <div key={step.status} className="flex flex-col items-center" style={{ width: '25%' }}>
                      <div
                        className={`w-20 h-20 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                          stepStatus === 'completed'
                            ? 'bg-gradient-to-br from-primary-500 to-secondary-500 text-white shadow-lg'
                            : stepStatus === 'current'
                            ? 'bg-gradient-to-br from-primary-500 to-secondary-500 text-white ring-4 ring-primary-200 shadow-lg'
                            : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                        }`}
                      >
                        <Icon className="w-8 h-8" />
                      </div>
                      <span
                        className={`mt-4 text-sm font-semibold text-center ${
                          stepStatus === 'completed' || stepStatus === 'current'
                            ? 'text-gray-900'
                            : 'text-gray-400'
                        }`}
                      >
                        {step.label}
                      </span>
                      <span className="text-xs text-gray-500 mt-1 text-center">
                        {step.description}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile Progress */}
            <div className="md:hidden space-y-4">
              {orderSteps.map((step, index) => {
                const stepStatus = getStepStatus(step.status);
                const Icon = step.icon;

                return (
                  <div key={step.status} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          stepStatus === 'completed'
                            ? 'bg-gradient-to-br from-primary-500 to-secondary-500 text-white'
                            : stepStatus === 'current'
                            ? 'bg-gradient-to-br from-primary-500 to-secondary-500 text-white ring-4 ring-primary-200'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      {index < orderSteps.length - 1 && (
                        <div className={`w-0.5 h-8 ${stepStatus === 'completed' ? 'bg-primary-500' : 'bg-gray-200'}`} />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <h4 className={`font-semibold ${stepStatus === 'upcoming' ? 'text-gray-400' : 'text-gray-900'}`}>
                        {step.label}
                      </h4>
                      <p className="text-sm text-gray-500">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cancelled Order Notice */}
        {order.status === 'cancelled' && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6 animate-slide-up">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-700">Order Cancelled</h3>
                <p className="text-red-600">
                  This order has been cancelled. Please contact support for more information.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Product Details - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-slide-up">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Product Details</h2>
              <div className="flex gap-5">
                <div className="w-32 h-32 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                  {order.product?.image_url ? (
                    <img
                      src={getImageUrl(order.product.image_url)}
                      alt={order.product?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-xl mb-2">{order.product?.name || 'Product'}</h3>
                  {order.product?.category?.name && (
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full mb-3">
                      {order.product.category.name}
                    </span>
                  )}
                  {order.product?.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{order.product.description}</p>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Quantity</p>
                      <p className="font-bold text-gray-900">{order.quantity} {order.product?.unit || 'units'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Unit Price</p>
                      <p className="font-bold text-gray-900">{formatPrice(order.unit_price)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-slide-up">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal ({order.quantity} items)</span>
                  <span>{formatPrice(order.unit_price * order.quantity)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>GST (Included)</span>
                  <span>-</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="text-lg font-bold text-gray-900">Total Amount</span>
                  <span className="text-xl font-bold text-primary-600">{formatPrice(order.total_amount)}</span>
                </div>
              </div>
            </div>

            {/* Status History */}
            {order.statusHistory && order.statusHistory.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-slide-up">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Status History</h2>
                <div className="space-y-4">
                  {order.statusHistory.map((history, index) => (
                    <div
                      key={history.id || index}
                      className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          index === 0 ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {getStatusIcon(history.status)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 capitalize">{history.status}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBgColor(history.status)}`}>
                            {history.status}
                          </span>
                        </div>
                        {history.remarks && (
                          <p className="text-gray-600 text-sm">{history.remarks}</p>
                        )}
                        <p className="text-gray-400 text-xs mt-1">
                          {formatDate(history.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Delivery & Supplier Info */}
          <div className="space-y-6">
            {/* Delivery Address */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-slide-up">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Delivery Address</h2>
              <div className="space-y-4">
                {/* Customer Name */}
                {order.customer_name && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Name</p>
                      <p className="font-medium text-gray-900">{order.customer_name}</p>
                    </div>
                  </div>
                )}

                {/* Email & Phone */}
                <div className="space-y-3">
                  {order.customer_email && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Email</p>
                        <p className="text-sm text-gray-900">{order.customer_email}</p>
                      </div>
                    </div>
                  )}

                  {order.customer_phone && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Phone</p>
                        <p className="text-sm text-gray-900">{order.customer_phone}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Address */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Shipping Address</p>
                    <p className="text-sm text-gray-900 leading-relaxed">
                      {order.shipping_address || order.delivery_address || order.customer?.address || 'Address not specified'}
                    </p>
                    {(order.shipping_city || order.shipping_state || order.shipping_pincode) && (
                      <p className="text-sm text-gray-600 mt-1">
                        {order.shipping_city}{order.shipping_state && `, ${order.shipping_state}`}
                        {order.shipping_pincode && ` - ${order.shipping_pincode}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Supplier Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-slide-up">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Supplier Details</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-secondary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {order.supplier?.company_name || 'Loha Mart'}
                    </p>
                    {order.supplier?.city && (
                      <p className="text-gray-600 text-sm">
                        {order.supplier.city}{order.supplier.state && `, ${order.supplier.state}`}
                      </p>
                    )}
                  </div>
                </div>
                {order.supplier?.contact_person && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Contact Person</p>
                      <p className="font-medium text-gray-900">{order.supplier.contact_person}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Need Help */}
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl border-2 border-primary-200 p-6 animate-slide-up">
              <h3 className="font-bold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-gray-600 text-sm mb-4">
                Contact our support team for any queries regarding your order.
              </p>
              <Button
                variant="primary"
                size="sm"
                fullWidth
                onClick={() => navigate('/contact')}
                leftIcon={<Phone className="w-4 h-4" />}
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
